import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Activity, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";
import { formatErr } from "@/lib/auth";

const Shell = ({ testid, children }) => (
  <div className="terminal-bg flex items-center justify-center px-4">
    <div className="panel rise w-full max-w-sm space-y-4 p-7" data-testid={testid}>
      <Link to="/" className="flex items-center gap-2">
        <Activity className="h-5 w-5 text-[#00E5FF]" />
        <span className="font-display font-bold">WHALE<span className="text-[#00E5FF]">/</span>FLOW</span>
      </Link>
      {children}
    </div>
  </div>
);

const btn = "w-full rounded-md bg-[#00E5FF] py-2.5 font-semibold text-[#080B10] transition-opacity hover:opacity-90 disabled:opacity-50";
const inp = "border-[#2E3C54] bg-[#0F141C]";

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await axios.post(`${API}/auth/forgot-password`, { email }); setSent(true); } catch (e2) { setErr(formatErr(e2)); }
    setBusy(false);
  };

  return (
    <Shell testid="forgot-page">
      <h1 className="font-display text-2xl font-bold">Forgot password</h1>
      {sent ? (
        <div className="space-y-3" data-testid="forgot-success">
          <MailCheck className="h-8 w-8 text-[#00E676]" />
          <p className="text-sm text-slate-300">If an account exists for <span className="font-mono text-slate-100">{email}</span>, a reset link is on its way. It expires in 1 hour; check your spam folder too.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-slate-400">Enter your account email and we'll send you a link to set a new password.</p>
          <Input data-testid="forgot-email-input" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} />
          {err && <p className="text-sm text-[#FF3D00]" data-testid="forgot-error">{err}</p>}
          <button data-testid="forgot-submit-button" disabled={busy} className={btn}>{busy ? "Sending…" : "Send reset link"}</button>
        </form>
      )}
      <p className="text-center text-sm text-slate-400">
        <Link to="/login" data-testid="forgot-back-login" className="text-[#00E5FF] hover:underline">Back to log in</Link>
      </p>
    </Shell>
  );
}

export function ResetPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setErr("Passwords don't match");
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: pw });
      toast.success("Password updated. Log in with your new password.");
      nav("/login");
    } catch (e2) { setErr(formatErr(e2)); }
    setBusy(false);
  };

  return (
    <Shell testid="reset-page">
      <h1 className="font-display text-2xl font-bold">Set a new password</h1>
      {!token ? (
        <p className="text-sm text-[#FF3D00]" data-testid="reset-error">Missing reset token. Please use the link from your email.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Input data-testid="reset-password-input" type="password" required minLength={6} placeholder="New password (min 6 chars)" value={pw} onChange={(e) => setPw(e.target.value)} className={inp} />
          <Input data-testid="reset-password-confirm-input" type="password" required minLength={6} placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={inp} />
          {err && <p className="text-sm text-[#FF3D00]" data-testid="reset-error">{err}</p>}
          <button data-testid="reset-submit-button" disabled={busy} className={btn}>{busy ? "Saving…" : "Update password"}</button>
        </form>
      )}
      <p className="text-center text-sm text-slate-400">
        <Link to="/forgot-password" data-testid="reset-request-new" className="text-[#00E5FF] hover:underline">Request a new link</Link>
      </p>
    </Shell>
  );
}
